/**
 * Возврат из Google: `/auth/google/callback?code=…&state=…`.
 *
 * Адрес должен один в один совпадать с прописанным в Google — менять только
 * вместе с devops. Страница сразу меняет code на сессию и убирает его из
 * адреса: code одноразовый, после перезагрузки с ним бэк ответит 502.
 */
import { takeReturnTo } from '@/shared/lib/returnTo';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  exchangeGoogleCode,
  useSelectGoogleTenant,
  type AuthTenant,
} from '@/entities/auth';
import { getApiErrorMessage, getApiErrorStatus } from '@/shared/api';

import { AuthShell } from './AuthShell';
import { AuthNotice } from './login/AuthNotice';
import { GoogleLoginButton } from './login/GoogleLoginButton';
import { TenantList } from './login/TenantList';
import { AUTH_HINT, AUTH_TEXT } from './texts';

type View =
  | { name: 'exchanging' }
  | { name: 'select_tenant'; selectToken: string; tenants: AuthTenant[] }
  | { name: 'not_found' }
  | { name: 'failed'; message: string; canRetry: boolean };

function failedView(error: unknown): View {
  // 501 — Google на сервере не настроен, повторять бессмысленно.
  if (getApiErrorStatus(error) === 501) {
    return {
      name: 'failed',
      message: 'Вход через Google не настроен на сервере',
      canRetry: false,
    };
  }

  return { name: 'failed', message: AUTH_TEXT.googleFailed, canRetry: true };
}

const backToLogin = (
  <Link
    to="/login"
    className="font-medium text-[var(--primary)] hover:underline"
  >
    Ко входу
  </Link>
);

export default function GoogleCallbackPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [view, setView] = useState<View>({ name: 'exchanging' });
  const selectTenant = useSelectGoogleTenant();
  const startedRef = useRef(false);

  useEffect(() => {
    // StrictMode в dev запускает эффект дважды, а code одноразовый.
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    const code = params.get('code');
    const state = params.get('state');

    if (!code || !state) {
      // Например, пользователь отказался на странице согласия Google.
      setView({
        name: 'failed',
        message: AUTH_TEXT.googleFailed,
        canRetry: true,
      });
      return;
    }

    exchangeGoogleCode({ code, state }).then(
      (response) => {
        if (response.status === 'logged_in') {
          toast.success('С возвращением');
          navigate(takeReturnTo(), { replace: true });
          return;
        }
        if (response.status === 'select_tenant') {
          setView({
            name: 'select_tenant',
            selectToken: response.select_token,
            tenants: response.tenants,
          });
          return;
        }
        setView({ name: 'not_found' });
      },
      (error: unknown) => setView(failedView(error))
    );

    // code одноразовый — убираем его из адреса сразу после запроса.
    window.history.replaceState(null, '', '/');
  }, [navigate, params]);

  if (view.name === 'select_tenant') {
    const { selectToken, tenants } = view;

    return (
      <AuthShell
        title="Выберите компанию"
        subtitle="Почта Google заведена в нескольких компаниях"
      >
        <TenantList
          tenants={tenants}
          pendingTenantId={
            selectTenant.isPending ? selectTenant.variables.tenant_id : null
          }
          onSelect={(tenant) =>
            selectTenant.mutate(
              { select_token: selectToken, tenant_id: tenant.tenant_id },
              {
                onSuccess: () => {
                  toast.success('С возвращением');
                  navigate(takeReturnTo(), { replace: true });
                },
                onError: (error) => {
                  // select_token живёт 5 минут — дальше только вход заново.
                  if (getApiErrorStatus(error) === 401) {
                    setView({
                      name: 'failed',
                      message:
                        'Время на выбор компании истекло. Войдите через Google ещё раз.',
                      canRetry: true,
                    });
                    return;
                  }
                  toast.error(
                    getApiErrorMessage(error, 'Не удалось войти в компанию')
                  );
                },
              }
            )
          }
        />
      </AuthShell>
    );
  }

  if (view.name === 'not_found') {
    return (
      <AuthShell
        title="Вход через Google"
        subtitle="Аккаунт не найден"
        footer={backToLogin}
      >
        <AuthNotice text={AUTH_TEXT.notFound} hint={AUTH_HINT.notFound} />
      </AuthShell>
    );
  }

  if (view.name === 'failed') {
    return (
      <AuthShell
        title="Вход через Google"
        subtitle="Не получилось войти"
        footer={backToLogin}
      >
        <div className="grid gap-4">
          <AuthNotice tone="error" text={view.message} />
          {view.canRetry ? <GoogleLoginButton /> : null}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Вход через Google" subtitle="Проверяем ответ Google…">
      <div className="flex justify-center py-6 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    </AuthShell>
  );
}
